// Design system — components พื้นฐาน (ใช้คู่กับ src/styles/components.css)
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import Icon from "./Icon";
import { avatarUri } from "../../assets/avatars";
import { characterPic, sizedSrc } from "../../assets/characterImages";
import { ART_BY_ICON } from "../../assets/illustrations";

export { default as Icon } from "./Icon";

const cx = (...a) => a.filter(Boolean).join(" ");

export function Button({ variant, size, block, icon, className, children, type = "button", ...rest }) {
  return (
    <button type={type} className={cx("btn", variant && `btn--${variant}`, size && `btn--${size}`, block && "btn--block", icon && "btn--icon", className)} {...rest}>
      {children}
    </button>
  );
}

export const Input = React.forwardRef(function Input({ label, hint, error, multiline, type = "text", id, className, ...rest }, ref) {
  const [show, setShow] = React.useState(false);
  const autoId = React.useId();
  const fid = id || autoId;
  const isPassword = type === "password";
  const Tag = multiline ? "textarea" : "input";
  return (
    <div className={cx("field", error && "field--error", className)}>
      {label ? <label className="field__label" htmlFor={fid}>{label}</label> : null}
      <div className="field__wrap">
        <Tag ref={ref} id={fid} className="field__control" type={multiline ? undefined : isPassword && show ? "text" : type} aria-invalid={!!error} {...rest} />
        {isPassword ? (
          <Button size="sm" icon variant="ghost" className="field__toggle" aria-label={show ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"} onClick={() => setShow((v) => !v)} style={{ border: 0 }}>
            <Icon name={show ? "eyeoff" : "eye"} size={18} />
          </Button>
        ) : null}
      </div>
      {error ? <span className="error-text" role="alert">{error}</span> : hint ? <span className="field__hint">{hint}</span> : null}
    </div>
  );
});

export function Modal({ open, title, onClose, children, footer, size, sheet = true }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    ref.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className={cx("overlay", sheet && "overlay--sheet")} onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className={cx("modal", size && `modal--${size}`)} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={ref}>
        <div className="modal__head">
          <h3>{title}</h3>
          <Button size="sm" icon variant="ghost" aria-label="ปิด" onClick={onClose}><Icon name="x" /></Button>
        </div>
        <div className="modal__body">{children}</div>
        {footer ? <div className="modal__foot">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
}

export function Tabs({ tabs, value, onChange, variant, className }) {
  return (
    <div className={cx("tabs", variant === "line" && "tabs--line", className)} role="tablist">
      {tabs.map((t) => (
        <button key={t.value} role="tab" aria-selected={value === t.value} className={cx("tab", value === t.value && "is-active")} onClick={() => onChange(t.value)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Chip({ active, onClick, size, children, className, ...rest }) {
  const Tag = onClick ? "button" : "span";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} className={cx("chip", size && `chip--${size}`, active && "is-active", className)} aria-pressed={onClick ? !!active : undefined} {...rest}>
      {children}
    </Tag>
  );
}

// สีพื้นหลังอวาตาร์/ภาพตัวละครจากชื่อ (ไม่ต้องใช้รูปจริง)
export const gradientOf = (colors, seed = "") => {
  if (Array.isArray(colors) && colors.length === 2) return `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`;
  let h = 0;
  for (const ch of String(seed)) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return `linear-gradient(135deg, hsl(${h} 70% 55%), hsl(${(h + 60) % 360} 70% 45%))`;
};

/** อวาตาร์: มี src = รูปจริง ; ไม่มี = ตัวการ์ตูน DiceBear สร้างในเครื่องจาก seed (ผู้ใช้)
 *  character = อวาตาร์ของตัวละคร -> ไม่มี src ใช้ภาพจาก manifest ตาม hash(seed) แทน DiceBear */
export function Avatar({ name = "", seed, colors, size = 40, square, src, className, character }) {
  const pic = character ? characterPic({ image: src, id: seed || name }, "thumb") : src ? sizedSrc(src, "thumb") : "";
  const style = { "--size": `${size}px`, background: pic ? undefined : gradientOf(colors, seed || name) };
  return (
    <span className={cx("avatar", square && "avatar--square", character && "avatar--char", className)} style={style} aria-hidden={!name}>
      {pic ? <img src={pic} alt={name} loading="lazy" decoding="async" width={size} height={size} /> : <img className="avatar__gen" src={avatarUri(seed || name)} alt="" />}
    </span>
  );
}

export function Card({ glass, grad, link, className, children, as: Tag = "div", ...rest }) {
  return <Tag className={cx("card", glass && "card--glass", grad && "card--grad", link && "card--link", className)} {...rest}>{children}</Tag>;
}

export function Skeleton({ width = "100%", height = 16, radius, style }) {
  return <span className="skeleton" style={{ width, height, borderRadius: radius, ...style }} aria-hidden="true" />;
}

export function EmptyState({ icon = "🫧", art, title, children, action }) {
  const Art = art || ART_BY_ICON[icon];
  return (
    <div className="empty">
      {Art ? <Art className="empty__art" /> : <div className="empty__icon" aria-hidden="true">{icon}</div>}
      <div className="empty__title">{title}</div>
      {children ? <div>{children}</div> : null}
      {action}
    </div>
  );
}

export const fmtNum = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
  return String(v);
};
export const fmtCoins = (n) => (Number(n) || 0).toLocaleString("th-TH");
export const baht = (n) => `฿${(Number(n) || 0).toLocaleString("th-TH")}`;

export function CoinBadge({ amount, children }) {
  return (
    <span className="coin">
      <i className="coin__dot" aria-hidden="true" />
      {children ?? fmtCoins(amount)}
    </span>
  );
}

export function BottomNav({ items, isActive }) {
  return (
    <nav className="bottomnav" aria-label="เมนูหลัก">
      {items.map((it) => (
        <Link key={it.to} to={it.to} className={cx("bottomnav__item", isActive(it) && "is-active")} aria-current={isActive(it) ? "page" : undefined}>
          <span className="bottomnav__icon"><Icon name={it.icon} size={20} /></span>
          {it.label}
        </Link>
      ))}
    </nav>
  );
}
